/**
 * ScheduleShowcase — Premium renderer for schedule / agenda / calendar layouts.
 * Palette: sky blue primary + category accents.
 * Phase 8 — Athena Presentation Design System.
 */
import React, { useState } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, SectionCard, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  accent:       '#38bdf8',
  accentFade:   'rgba(56,189,248,0.10)',
  accentBorder: 'rgba(56,189,248,0.22)',
  card:         base.card,
  border:       base.border,
  text:         base.text,
  textMuted:    base.textMuted,
  textFaint:    base.textFaint,
};

const CAT_COLORS = {
  work:    { color: '#38bdf8', fade: 'rgba(56,189,248,0.12)',  border: 'rgba(56,189,248,0.25)' },
  health:  { color: '#34d399', fade: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.25)' },
  food:    { color: '#fb923c', fade: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.25)' },
  social:  { color: '#c084fc', fade: 'rgba(192,132,252,0.12)', border: 'rgba(192,132,252,0.25)' },
  study:   { color: '#fbbf24', fade: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.25)' },
  default: { color: '#94a3b8', fade: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.20)' },
};

const CAT_ICONS = { work: '💼', health: '🏃', food: '🍽️', social: '👥', study: '📚', default: '📌' };
const PRIO_DOT = { high: '#ef4444', medium: '#fbbf24', low: '#94a3b8' };

const _card = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 14,
  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
};

function HeroDesktop({ d }) {
  const totalEvents = (d.events || []).length ||
    (d.days || []).reduce((s, day) => s + (day.activities || []).length, 0);
  const viewLabel = { day: 'Diário', week: 'Semanal', month: 'Mensal' }[d.view] || 'Agenda';
  const titleFs   = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS = [
    { icon: '📋', value: viewLabel, label: 'Tipo' },
    { icon: '🗓', value: totalEvents > 0 ? `${totalEvents} eventos` : 'Programado', label: 'Status' },
    { icon: '📆', value: d.date_range || 'Planejado', label: 'Período' },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="📅 AGENDA"
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
        placeholder="📅" placeholderLabel="Agenda"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '0 0 6px', fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
          {d.date_range && <span style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>📆 {d.date_range}</span>}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.accent} />
      </div>
    </div>
  );
}

function HeroMobile({ d }) {
  const totalEvents = (d.events || []).length ||
    (d.days || []).reduce((s, day) => s + (day.activities || []).length, 0);
  const viewLabel = { day: 'Diário', week: 'Semanal', month: 'Mensal' }[d.view] || 'Agenda';
  const METRICS = [
    { icon: '📋', value: viewLabel,                                          label: 'Tipo'     },
    { icon: '🗓', value: totalEvents > 0 ? `${totalEvents}` : 'Completo',   label: 'Eventos'  },
    { icon: '📆', value: d.date_range || 'Planejado',                       label: 'Período'  },
    { icon: '📌', value: 'Agenda',                                           label: 'Tipo'     },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>📅 AGENDA</span>
          {d.date_range && <span style={{ fontSize: 11.5, color: C.accent, fontWeight: 600, marginBottom: 5, display: 'block' }}>📆 {d.date_range}</span>}
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
          placeholder="📅"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

function EventCard({ ev, compact }) {
  const cc = CAT_COLORS[ev.category] || CAT_COLORS.default;
  const icon = CAT_ICONS[ev.category] || CAT_ICONS.default;
  const dot = PRIO_DOT[ev.priority] || PRIO_DOT.medium;
  return (
    <div style={{
      display: 'flex', gap: 10, padding: compact ? '8px 10px' : '10px 12px',
      background: cc.fade, border: `1px solid ${cc.border}`,
      borderLeft: `3px solid ${cc.color}`, borderRadius: 8, marginBottom: 6,
      alignItems: 'flex-start',
    }}>
      <span style={{ fontSize: compact ? 14 : 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {ev.time && (
            <span style={{ fontSize: 11, fontWeight: 700, color: cc.color, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{ev.time}</span>
          )}
          <span style={{ fontSize: compact ? 13 : 14, fontWeight: 600, color: C.text, flex: 1 }}>{ev.title}</span>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        </div>
        {ev.location && (
          <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>📍 {ev.location}</div>
        )}
        {ev.description && (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.5 }}>{ev.description}</div>
        )}
      </div>
    </div>
  );
}

function DayBlock({ day, compact }) {
  const [open, setOpen] = useState(true);
  const acts = day.activities || [];
  return (
    <div style={{ ..._card, marginBottom: 10, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: C.text,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: C.accent }}>{day.day}</span>
          {day.date && day.date !== day.day && (
            <span style={{ fontSize: 11, color: C.textFaint }}>{day.date}</span>
          )}
          <span style={{ fontSize: 11, color: C.textFaint, background: 'rgba(148,163,184,0.1)', borderRadius: 10, padding: '1px 7px' }}>
            {acts.length} {acts.length === 1 ? 'evento' : 'eventos'}
          </span>
        </div>
        <span style={{ fontSize: 12, color: C.textFaint }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          {acts.length > 0
            ? acts.map((act, i) => <EventCard key={i} ev={act} compact={compact} />)
            : <p style={{ margin: 0, fontSize: 13, color: C.textFaint, fontStyle: 'italic' }}>Dia livre</p>
          }
        </div>
      )}
    </div>
  );
}

export default function ScheduleShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  const events = d.events || [];
  const days   = d.days   || [];

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <HeroMobile d={d} /> : <HeroDesktop d={d} />}

      {d.context && !d.subtitle && (
        <div style={{ ..._card, padding: '12px 14px', marginBottom: 12, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
          {d.context}
        </div>
      )}

      {d.summary && (
        <SectionCard title="Destaques" borderColor={C.accentBorder} style={{ marginBottom: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.summary}</p>
        </SectionCard>
      )}

      {days.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {days.map((day, i) => <DayBlock key={i} day={day} compact={isMobile} />)}
        </div>
      )}

      {events.length > 0 && days.length === 0 && (
        <div style={{ ..._card, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 10 }}>
            Eventos ({events.length})
          </div>
          {events.map((ev, i) => <EventCard key={i} ev={ev} compact={isMobile} />)}
        </div>
      )}

      {(d.sections || []).map((s, i) => (
        <SectionCard key={i} title={s.title} borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.accent} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </div>
  );
}
